import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, X, ArrowRight, CornerDownLeft } from 'lucide-react';
import { api } from '../api/client';
import useDebounce from '../hooks/useDebounce';
import BillableBadge from './BillableBadge';
import { Spinner } from './Spinner';
import { useTypewriter } from './motion/TypewriterPlaceholder';

const CODE_RE = /^(?:[A-TV-Z][0-9]|QA)[0-9A-Z](?:\.?[0-9A-Z]{1,4})?$/i;

/** "e1165" / "E11.65" / "E11.-" → "E11.65" / "E11"; anything that isn't code-shaped → null. */
export function normalizeCodeInput(str) {
  if (!str) return null;
  const s = String(str).trim().toUpperCase().replace(/\s+/g, '').replace(/[.-]+$/, '');
  if (!CODE_RE.test(s)) return null;
  const noDot = s.replace(/\./g, '');
  return noDot.length > 3 ? `${noDot.slice(0, 3)}.${noDot.slice(3)}` : noDot;
}

const SIZES = {
  sm: {
    input:
      'h-10 rounded-xl border border-on-navy/15 bg-on-navy/[0.07] pl-10 pr-12 text-[14.5px] font-medium text-on-navy placeholder:text-on-navy/60 hover:border-on-navy/20 focus:border-transparent focus:bg-surface focus:text-ink focus:ring-4 focus:ring-electric/40 focus:placeholder:text-muted/60',
    icon: 'left-3.5 h-4 w-4 text-on-navy/65 group-focus-within/sb:text-primary',
    button: null,
  },
  md: {
    input:
      'h-12 rounded-2xl border border-line/40 bg-surface pl-12 pr-28 text-base font-medium text-ink shadow-soft placeholder:text-muted/60 hover:border-line/70 focus:border-primary/60 focus:ring-4 focus:ring-primary/15',
    icon: 'left-4 h-5 w-5 text-muted group-focus-within/sb:text-primary',
    button: 'btn-primary btn-sm absolute right-1.5 top-1/2 h-9 -translate-y-1/2 rounded-xl hover:-translate-y-1/2 active:-translate-y-1/2',
  },
  lg: {
    input:
      'h-16 rounded-2xl border-2 border-navy bg-surface pl-14 pr-[4.5rem] text-lg font-medium text-ink shadow-lift placeholder:text-muted/65 focus:border-primary/50 focus:ring-[6px] focus:ring-electric/25 dark:border-line/40 sm:h-[4.5rem] sm:pr-44 sm:text-xl',
    icon: 'left-5 h-6 w-6 text-primary',
    button: 'btn-pop absolute right-2 top-1/2 h-12 -translate-y-1/2 rounded-xl px-3.5 hover:-translate-y-1/2 active:-translate-y-1/2 sm:h-14 sm:px-6',
  },
};

const DEFAULT_PHRASES = ['E11.65', 'T2DM with CKD stage 3', 'HTN', 'fracture of left femur', 'S72.001A', 'COPD exacerbation'];

function escapeRe(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Wraps the parts of `text` matching words of `query` in <mark>. Exported for reuse. */
export function Highlight({ text, query, className = 'bg-transparent font-bold text-primary' }) {
  const words = String(query || '')
    .trim()
    .split(/\s+/)
    .filter((w) => w.length >= 2)
    .map(escapeRe);
  if (!words.length || !text) return text || null;
  const parts = String(text).split(new RegExp(`(${words.join('|')})`, 'ig'));
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className={className}>
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

function isTypingTarget(el) {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

export default function SearchBar({ autoFocus = false, size = 'md', placeholder, phrases }) {
  const navigate = useNavigate();
  const location = useLocation();
  const baseId = useId();
  const listId = `${baseId}-list`;
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const reqRef = useRef(0);

  const [q, setQ] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [focused, setFocused] = useState(false);

  const styles = SIZES[size] || SIZES.md;
  const debounced = useDebounce(q.trim(), 250);

  // Keep the box in sync with the URL on /search, clear it elsewhere after navigation.
  useEffect(() => {
    if (location.pathname === '/search') {
      setQ(new URLSearchParams(location.search).get('q') || '');
    } else {
      setQ('');
    }
    setOpen(false);
    setActive(-1);
  }, [location.pathname, location.search]);

  // Debounced autocomplete.
  useEffect(() => {
    if (debounced.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const reqId = ++reqRef.current;
    setLoading(true);
    api
      .get('/search/autocomplete', { params: { q: debounced }, signal: controller.signal })
      .then(({ data }) => {
        if (reqId !== reqRef.current) return;
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions.slice(0, 10) : []);
        setActive(-1);
      })
      .catch(() => {
        if (reqId === reqRef.current) setSuggestions([]);
      })
      .finally(() => {
        if (reqId === reqRef.current) setLoading(false);
      });
    return () => controller.abort();
  }, [debounced]);

  // Global "/" shortcut — only the first visible search input on the page reacts.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(document.activeElement)) return;
      const inputs = Array.from(document.querySelectorAll('input[data-global-search]'));
      const firstVisible = inputs.find((el) => el.offsetParent !== null && el.getClientRects().length > 0);
      if (firstVisible && firstVisible === inputRef.current) {
        e.preventDefault();
        inputRef.current.focus();
        inputRef.current.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const trimmed = q.trim();
  const showAllIndex = suggestions.length; // index of the "See all results" option
  const optionCount = trimmed.length >= 2 ? suggestions.length + 1 : 0;
  const settled = debounced === trimmed && !loading;
  const listVisible = open && trimmed.length >= 2 && (suggestions.length > 0 || settled);

  const goToCode = useCallback(
    (code) => {
      setOpen(false);
      inputRef.current && inputRef.current.blur();
      navigate(`/icd10/${encodeURIComponent(code)}`);
    },
    [navigate]
  );

  const submit = useCallback(() => {
    const value = q.trim();
    if (!value) return;
    setOpen(false);
    inputRef.current && inputRef.current.blur();
    const code = normalizeCodeInput(value);
    if (code) navigate(`/icd10/${encodeURIComponent(code)}`);
    else navigate(`/search?q=${encodeURIComponent(value)}`);
  }, [q, navigate]);

  const seeAll = useCallback(() => {
    const value = q.trim();
    if (!value) return;
    setOpen(false);
    inputRef.current && inputRef.current.blur();
    navigate(`/search?q=${encodeURIComponent(value)}`);
  }, [q, navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      if (!optionCount) return;
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1 >= optionCount ? 0 : a + 1));
    } else if (e.key === 'ArrowUp') {
      if (!optionCount) return;
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a <= 0 ? optionCount - 1 : a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (listVisible && active >= 0 && active < suggestions.length) goToCode(suggestions[active].code);
      else if (listVisible && active === showAllIndex) seeAll();
      else submit();
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setActive(-1);
      } else if (q) {
        setQ('');
      } else {
        inputRef.current && inputRef.current.blur();
      }
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const activeId = listVisible && active >= 0 ? `${baseId}-opt-${active}` : undefined;
  const staticPh = placeholder || (size === 'sm' ? 'Search codes, conditions, abbreviations…' : size === 'lg' ? 'Code, condition or abbreviation…' : 'Search a code, term or abbreviation');
  const typing = size === 'lg' && !placeholder && !focused && !q;
  const typed = useTypewriter(phrases || DEFAULT_PHRASES, { paused: !typing, fallback: staticPh });
  const ph = typing ? `Try “${typed}”` : staticPh;
  const onDark = size === 'sm' && !focused;

  return (
    <div ref={wrapRef} className="group/sb relative w-full">
      {size === 'lg' && (
        <div
          aria-hidden="true"
          className={`pointer-events-none absolute inset-0 rounded-2xl transition-[transform,background-color] duration-300 ease-out-expo ${
            focused ? 'translate-x-2.5 translate-y-2.5 bg-pop' : 'translate-x-1.5 translate-y-1.5 bg-sun'
          }`}
        />
      )}
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="relative"
      >
        <label htmlFor={`${baseId}-input`} className="sr-only">
          Search ICD-10-CM codes
        </label>
        <Search className={`pointer-events-none absolute top-1/2 z-10 -translate-y-1/2 transition-colors ${styles.icon}`} aria-hidden="true" />
        <input
          ref={inputRef}
          id={`${baseId}-input`}
          data-global-search=""
          type="search"
          inputMode="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          role="combobox"
          aria-expanded={listVisible}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-haspopup="listbox"
          value={q}
          placeholder={ph}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => {
            setOpen(true);
            setFocused(true);
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
          className={`relative w-full appearance-none outline-none transition duration-200 [&::-webkit-search-cancel-button]:hidden ${styles.input}`}
        />
        <div className={`absolute top-1/2 z-10 flex -translate-y-1/2 items-center gap-1 ${styles.button ? (size === 'lg' ? 'right-16 sm:right-36' : 'right-24') : 'right-2.5'}`}>
          {loading && <Spinner className="h-4 w-4" />}
          {q && !loading && (
            <button
              type="button"
              onClick={() => {
                setQ('');
                setSuggestions([]);
                inputRef.current && inputRef.current.focus();
              }}
              className={`grid h-7 w-7 place-items-center rounded-full transition-colors ${onDark ? 'text-on-navy/70 hover:text-on-navy' : 'text-muted hover:bg-surface-2 hover:text-ink'}`}
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {!q && !loading && (
            <span className={`kbd hidden sm:inline-flex ${onDark ? 'border-on-navy/15 bg-on-navy/5 text-on-navy/60 shadow-none' : ''}`} aria-hidden="true">
              /
            </span>
          )}
        </div>
        {styles.button && (
          <button type="submit" className={`${styles.button} z-10`} aria-label="Search">
            <span className={size === 'lg' ? 'hidden sm:inline' : ''}>Search</span>
            {size === 'lg' && <ArrowRight className="h-5 w-5" />}
          </button>
        )}
      </form>

      <AnimatePresence>
        {listVisible && (
          <motion.div
            key="sb-list"
            initial={{ opacity: 0, y: -6, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.18, ease: [0.16, 1, 0.3, 1] } }}
            exit={{ opacity: 0, y: -4, transition: { duration: 0.1 } }}
            style={{ transformOrigin: 'top center' }}
            className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-2xl border border-line/25 bg-surface text-left text-ink shadow-lift"
          >
            <ul id={listId} role="listbox" aria-label="Code suggestions" className="max-h-[min(65vh,28rem)] overflow-auto p-1.5">
              {suggestions.length === 0 && settled && (
                <li className="flex items-center gap-2 px-3 py-3 text-sm text-muted" role="presentation">
                  <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
                  No quick matches — press Enter to run a full search.
                </li>
              )}
              {suggestions.map((s, i) => (
                <motion.li
                  key={s.code}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0, transition: { delay: Math.min(i, 8) * 0.025, duration: 0.2 } }}
                  id={`${baseId}-opt-${i}`}
                  role="option"
                  aria-selected={active === i}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => goToCode(s.code)}
                  onMouseEnter={() => setActive(i)}
                  className={`flex cursor-pointer items-start gap-3 rounded-xl px-2.5 py-2 transition-colors sm:px-3 ${active === i ? 'bg-sky-soft' : ''}`}
                >
                  <span className="code-pill mt-px shrink-0">{s.code}</span>
                  <span className="min-w-0 flex-1 text-[14.5px] leading-snug text-ink">
                    <Highlight text={s.title} query={trimmed} />
                    {s.chapterTitle && <span className="mt-0.5 block truncate text-xs text-muted">{s.chapterTitle}</span>}
                  </span>
                  <span className="hidden shrink-0 pt-0.5 sm:inline-flex">
                    <BillableBadge isBillable={s.isBillable} requires7th={s.requires7th} />
                  </span>
                </motion.li>
              ))}
              <li
                id={`${baseId}-opt-${showAllIndex}`}
                role="option"
                aria-selected={active === showAllIndex}
                onMouseDown={(e) => e.preventDefault()}
                onClick={seeAll}
                onMouseEnter={() => setActive(showAllIndex)}
                className={`mt-1 flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 font-display text-sm font-bold text-primary transition-colors ${active === showAllIndex ? 'bg-sky-soft' : ''}`}
              >
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-primary/10">
                  <Search className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <span className="truncate">See all results for “{trimmed}”</span>
                <ArrowRight className="ml-auto h-4 w-4 shrink-0" aria-hidden="true" />
              </li>
            </ul>
            <div className="hidden items-center gap-4 border-t border-line/15 bg-surface-2/60 px-4 py-2 text-[11.5px] text-muted sm:flex" aria-hidden="true">
              <span className="inline-flex items-center gap-1">
                <span className="kbd">↑</span>
                <span className="kbd">↓</span>
                navigate
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="kbd">
                  <CornerDownLeft className="h-3 w-3" />
                </span>
                open
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="kbd">esc</span>
                close
              </span>
              <span className="ml-auto">Exact codes jump straight to the detail page</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
